import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Spin, Typography } from 'antd';
import api from '../api';

// ── Time helpers ──────────────────────────────────────────────────────────────

// Accepts "HH:MM", "HH:MM:SS", or "YYYY-MM-DD HH:MM:SS"
function toMins(str) {
  if (!str) return null;
  const t = str.slice(-8);          // last 8 chars always = "HH:MM:SS" (or "HH:MM" padded by slice)
  const [h, m, s = '0'] = t.split(':');
  const v = +h * 60 + +m + +s / 60;
  return isNaN(v) ? null : v;
}

function fmtHM(str) {
  if (!str) return '?';
  return str.slice(-8).slice(0, 5);
}

function fmtDur(secs) {
  if (secs == null || secs < 0) return '';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// ── Color ─────────────────────────────────────────────────────────────────────

function stateColor(state) {
  if (!state) return '#8c8c8c';
  const s = state.toLowerCase();
  if (s === 'available')                          return '#52c41a';
  if (s === 'logged in')                          return '#1677ff';
  if (s === 'logged out')                         return '#434343';
  if (s === 'unavailable')                        return '#d9d9d9';
  if (s.includes('break'))                        return '#fa8c16';
  if (s.includes('lunch') || s.includes('meal'))  return '#ff85c2';
  if (s.includes('refused'))                      return '#ff4d4f';
  if (s.includes('technical'))                    return '#722ed1';
  if (s.startsWith('inbound'))                    return '#13c2c2';
  if (s.startsWith('outbound'))                   return '#2f54eb';
  return '#8c8c8c';
}

function stateFg(state) {
  const light = ['#52c41a', '#faad14', '#fa8c16', '#d9d9d9', '#13c2c2'];
  return light.includes(stateColor(state)) ? '#262626' : '#ffffff';
}

// ── Layout ────────────────────────────────────────────────────────────────────

const LABEL_W  = 88;
const PAD_R    = 20;
const AXIS_H   = 46;
const ROW_H    = 52;
const ROW_GAP  = 16;
const CR       = 4;   // corner radius
const MIN_W    = 1600; // minimum SVG width; container scrolls if viewport is narrower

const ALL_ROWS = [
  { key: 'schedule', label: 'Schedule' },
  { key: 'punches',  label: 'Punches'  },
  { key: 'states',   label: 'States'   },
];

const rowY = (i) => AXIS_H + i * (ROW_H + ROW_GAP);

// ── Legend items ──────────────────────────────────────────────────────────────

const LEGEND = [
  { label: 'Schedule',        color: '#91caff', border: true  },
  { label: 'Punches',         color: '#52c41a', alpha: 0.65   },
  { label: 'Available',       color: '#52c41a' },
  { label: 'Logged In',       color: '#1677ff' },
  { label: 'Inbound',         color: '#13c2c2' },
  { label: 'Outbound',        color: '#2f54eb' },
  { label: 'Break',           color: '#fa8c16' },
  { label: 'Lunch',           color: '#ff85c2' },
  { label: 'Unavailable',     color: '#d9d9d9', border: true  },
  { label: 'Refused Contact', color: '#ff4d4f' },
  { label: 'Technical Issue', color: '#722ed1' },
  { label: 'Logged Out',      color: '#434343' },
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function AgentTimeline({ agentUserId, date, hidePunches = false, hideSchedule = false, selectedStates = [], onStatesLoaded }) {
  const uid                     = useId();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const containerRef            = useRef(null);
  const [svgW, setSvgW]         = useState(900);

  const visibleRows = ALL_ROWS.filter(r =>
    !(r.key === 'schedule' && hideSchedule) &&
    !(r.key === 'punches'  && hidePunches)
  );
  const svgH      = rowY(visibleRows.length) + 12;
  const schedIdx  = visibleRows.findIndex(r => r.key === 'schedule');
  const punchIdx  = visibleRows.findIndex(r => r.key === 'punches');
  const stateIdx  = visibleRows.findIndex(r => r.key === 'states');

  // Track container width
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSvgW(e.contentRect.width || 900));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Fetch timeline data
  useEffect(() => {
    if (!agentUserId || !date) return;
    setLoading(true);
    setData(null);
    api.get('/api/agent/ccaas-timeline', {
      params: { user_id: agentUserId, date: date.format('YYYY-MM-DD') },
    })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [agentUserId, date]);

  // Derive time range from data
  const { rangeMin, rangeMax } = useMemo(() => {
    if (!data) return { rangeMin: 7 * 60, rangeMax: 18 * 60 };
    const pts = [];
    (data.states   || []).forEach((s) => {
      const a = toMins(s.StartDate); if (a != null) pts.push(a);
      const b = toMins(s.EndDate);   if (b != null) pts.push(b);
    });
    (data.punches  || []).forEach((p) => {
      const a = toMins(p.in);  if (a != null) pts.push(a);
      const b = toMins(p.out); if (b != null) pts.push(b);
    });
    (data.schedule || []).forEach((s) => {
      const a = toMins(s.ShiftStart); if (a != null) pts.push(a);
      const b = toMins(s.ShiftEnd);   if (b != null) pts.push(b);
    });
    if (!pts.length) return { rangeMin: 7 * 60, rangeMax: 18 * 60 };
    const lo = Math.min(...pts);
    const hi = Math.max(...pts);
    return {
      rangeMin: Math.max(0,    Math.floor((lo - 30) / 15) * 15),
      rangeMax: Math.min(1440, Math.ceil ((hi + 30) / 15) * 15),
    };
  }, [data]);

  const effectiveW = Math.max(MIN_W, svgW);
  const drawW = Math.max(100, effectiveW - LABEL_W - PAD_R);
  const span  = Math.max(1, rangeMax - rangeMin);

  const toX = useCallback(
    (m) => LABEL_W + ((m - rangeMin) / span) * drawW,
    [rangeMin, span, drawW],
  );

  // 15-min tick positions
  const ticks = useMemo(() => {
    const out = [];
    for (let m = Math.ceil(rangeMin / 15) * 15; m <= rangeMax; m += 15) out.push(m);
    return out;
  }, [rangeMin, rangeMax]);

  // Tooltip handlers
  const showTip = (e, lines) => setTooltip({ x: e.clientX, y: e.clientY, lines });
  const moveTip = (e) => setTooltip((t) => t ? { ...t, x: e.clientX, y: e.clientY } : null);
  const hideTip = () => setTooltip(null);

  const isEmpty = !data || (!data.states?.length && !data.punches?.length && !data.schedule?.length);

  const distinctStates = useMemo(() => {
    if (!data?.states?.length) return [];
    return [...new Set(data.states.map((s) => s.State).filter(Boolean))].sort();
  }, [data]);

  useEffect(() => {
    onStatesLoaded?.(distinctStates);
  }, [distinctStates]); // eslint-disable-line

  const visibleStates = useMemo(() => {
    if (!data?.states) return [];
    if (!selectedStates.length) return data.states;
    return data.states.filter((s) => selectedStates.includes(s.State));
  }, [data, selectedStates]);

  return (
    <div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>
      ) : isEmpty ? (
        <Typography.Text type="secondary">No data for this date.</Typography.Text>
      ) : (
        <>
          {/* SVG Timeline */}
          <div ref={containerRef} style={{ width: '100%', overflowX: 'auto' }}>
            <svg width={effectiveW} height={svgH} style={{ display: 'block', fontFamily: 'inherit' }}>

              {/* Grid lines */}
              {ticks.map((m) => {
                const x      = toX(m);
                const isHour = m % 60 === 0;
                return (
                  <line key={m}
                    x1={x} y1={AXIS_H - 12} x2={x} y2={svgH - 10}
                    stroke={isHour ? '#d9d9d9' : '#f0f0f0'}
                    strokeWidth={isHour ? 1 : 0.5}
                  />
                );
              })}

              {/* Hour tick marks on axis */}
              {ticks.filter((m) => m % 60 === 0).map((m) => (
                <line key={`t${m}`}
                  x1={toX(m)} y1={AXIS_H - 12} x2={toX(m)} y2={AXIS_H - 4}
                  stroke="#bfbfbf" strokeWidth={1}
                />
              ))}

              {/* Quarter-hour tick marks */}
              {ticks.filter((m) => m % 60 !== 0).map((m) => (
                <line key={`t${m}`}
                  x1={toX(m)} y1={AXIS_H - 8} x2={toX(m)} y2={AXIS_H - 4}
                  stroke="#d9d9d9" strokeWidth={0.5}
                />
              ))}

              {/* Hour labels */}
              {ticks.filter((m) => m % 60 === 0).map((m) => {
                const h = Math.floor(m / 60);
                const label = `${h % 12 || 12}${h < 12 ? 'am' : 'pm'}`;
                return (
                  <text key={`l${m}`}
                    x={toX(m)} y={AXIS_H - 15}
                    textAnchor="middle" fontSize={12} fill="#8c8c8c">
                    {label}
                  </text>
                );
              })}

              {/* Row backgrounds + labels */}
              {visibleRows.map(({ key, label }, i) => (
                <g key={key}>
                  <rect x={LABEL_W} y={rowY(i)} width={drawW} height={ROW_H}
                    fill={i % 2 === 0 ? '#fafafa' : '#f5f5f5'} />
                  <text x={LABEL_W - 6} y={rowY(i) + ROW_H / 2 + 4}
                    textAnchor="end" fontSize={13} fill="#8c8c8c">
                    {label}
                  </text>
                </g>
              ))}

              {/* ── Schedule bands ── */}
              {schedIdx >= 0 && (data.schedule || []).map((s, i) => {
                const x1 = toX(toMins(s.ShiftStart));
                const x2 = toX(toMins(s.ShiftEnd));
                const w  = Math.max(2, x2 - x1);
                const y  = rowY(schedIdx);
                return (
                  <rect key={i}
                    x={x1} y={y + 4} width={w} height={ROW_H - 8}
                    fill="#91caff" fillOpacity={0.75} rx={CR}
                    style={{ cursor: 'default' }}
                    onMouseEnter={(e) => showTip(e, [`Scheduled: ${s.ShiftStart} – ${s.ShiftEnd}`])}
                    onMouseMove={moveTip}
                    onMouseLeave={hideTip}
                  />
                );
              })}

              {/* ── Punch bands ── */}
              {punchIdx >= 0 && (data.punches || []).map((p, i) => {
                const x1 = toX(toMins(p.in));
                const x2 = toX(toMins(p.out));
                const w  = Math.max(2, x2 - x1);
                const y  = rowY(punchIdx);
                return (
                  <rect key={i}
                    x={x1} y={y + 4} width={w} height={ROW_H - 8}
                    fill="#52c41a" fillOpacity={0.65} rx={CR}
                    style={{ cursor: 'default' }}
                    onMouseEnter={(e) => showTip(e, [
                      `Punch In:  ${fmtHM(p.in)}`,
                      `Punch Out: ${fmtHM(p.out)}`,
                    ])}
                    onMouseMove={moveTip}
                    onMouseLeave={hideTip}
                  />
                );
              })}

              {/* ── State bands ── */}
              {visibleStates.map((s, i) => {
                const x1    = toX(toMins(s.StartDate));
                const endM  = s.EndDate ? toMins(s.EndDate) : rangeMax;
                const x2    = toX(endM);
                const w     = Math.max(2, x2 - x1);
                const y     = rowY(stateIdx);
                const fill  = stateColor(s.State);
                const fg    = stateFg(s.State);
                const clipId = `${uid}-cs${i}`;
                return (
                  <g key={i}>
                    <clipPath id={clipId}>
                      <rect x={x1} y={y + 2} width={w} height={ROW_H - 4} />
                    </clipPath>
                    <rect
                      x={x1} y={y + 2} width={w} height={ROW_H - 4}
                      fill={fill} rx={CR}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => showTip(e, [
                        s.State,
                        `${fmtHM(s.StartDate)} – ${s.EndDate ? fmtHM(s.EndDate) : '?'}`,
                        ...(s.DurationSecs != null ? [fmtDur(s.DurationSecs)] : []),
                      ])}
                      onMouseMove={moveTip}
                      onMouseLeave={hideTip}
                    />
                    {/* Label inside bar when wide enough */}
                    {w > 36 && (
                      <text
                        x={x1 + 5} y={y + ROW_H / 2 + 4}
                        clipPath={`url(#${clipId})`}
                        fontSize={11} fill={fg}
                        style={{ pointerEvents: 'none', userSelect: 'none' }}>
                        {s.State}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginTop: 14, paddingLeft: LABEL_W }}>
            {LEGEND.filter(({ label }) =>
              !(label === 'Schedule' && hideSchedule) &&
              !(label === 'Punches'  && hidePunches)
            ).map(({ label, color, border, alpha }) => (
              <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#595959' }}>
                <span style={{
                  width: 14, height: 14, borderRadius: 2,
                  background: color,
                  opacity: alpha ?? 1,
                  display: 'inline-block',
                  border: border ? '1px solid #d9d9d9' : 'none',
                  flexShrink: 0,
                }} />
                {label}
              </span>
            ))}
          </div>
        </>
      )}

      {/* Floating tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed',
          left: Math.min(tooltip.x + 14, window.innerWidth - 230),
          top:  tooltip.y - 10,
          background: 'rgba(0,0,0,0.8)',
          color: '#fff',
          padding: '5px 10px',
          borderRadius: 5,
          fontSize: 12,
          lineHeight: '20px',
          pointerEvents: 'none',
          zIndex: 9999,
          whiteSpace: 'pre',
        }}>
          {tooltip.lines.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      )}
    </div>
  );
}
